import type { DecisionLedgerRealizationCommitmentOwnerDto } from './decision-ledger';

export const STRATEGY_COMMITMENT_DRAFT_SCHEMA_VERSION =
  'strategy_commitment_draft_v1' as const;

export const STRATEGY_COMMITMENT_DRAFT_ARTIFACT_POSTURE = 'planning_view' as const;

export const STRATEGY_COMMITMENT_DRAFT_METRIC_KEYS = Object.freeze([
  'operating_income_usd',
  'ebitda_usd',
  'revenue_usd',
  'gross_margin_pct',
  'cost_to_serve_pct',
  'sga_cost_usd',
  'labor_cost_run_rate_usd',
  'headcount',
  'capacity_gap_fte',
  'on_time_delivery_pct',
  'customer_retention_pct',
  'cash_conversion_cycle_days',
] as const);

export type StrategyCommitmentDraftMetricKey =
  (typeof STRATEGY_COMMITMENT_DRAFT_METRIC_KEYS)[number];

export const STRATEGY_COMMITMENT_DRAFT_METRIC_UNITS = Object.freeze([
  'USD',
  'PCT',
  'COUNT',
  'FTE',
  'DAYS',
  'BPS',
] as const);

export type StrategyCommitmentDraftMetricUnit =
  (typeof STRATEGY_COMMITMENT_DRAFT_METRIC_UNITS)[number];

export const STRATEGY_COMMITMENT_DRAFT_WATCH_STREAMS = Object.freeze([
  'finance_pnl',
  'initiative_delivery',
  'cost_to_serve',
  'customer_service',
  'delivery_events',
  'workforce_plan',
  'capacity',
  'pricing',
  'claims',
] as const);

export type StrategyCommitmentDraftWatchStream =
  (typeof STRATEGY_COMMITMENT_DRAFT_WATCH_STREAMS)[number];

export type StrategyCommitmentDraftMetricValueV1 = {
  value: number;
  unit: StrategyCommitmentDraftMetricUnit;
  text: string;
};

export type StrategyCommitmentDraftSourceCitationV1 = {
  page: number;
  section: string;
};

export type StrategyCommitmentDraftV1 = {
  schemaVersion: typeof STRATEGY_COMMITMENT_DRAFT_SCHEMA_VERSION;
  artifactPosture: typeof STRATEGY_COMMITMENT_DRAFT_ARTIFACT_POSTURE;
  intentText: string;
  metricKey: StrategyCommitmentDraftMetricKey;
  /** Supplied by a governed loader or human edit; provider extraction must not invent it. */
  initiativeId?: string;
  /** Supplied by a governed loader or human edit; validated against tenant membership on approval. */
  ownerUserId?: string;
  ownerPosition?: DecisionLedgerRealizationCommitmentOwnerDto['ownerPosition'];
  baseline?: StrategyCommitmentDraftMetricValueV1;
  target: StrategyCommitmentDraftMetricValueV1;
  targetDate: string;
  ownerHint: string;
  watchStreams: StrategyCommitmentDraftWatchStream[];
  sourceCitation: StrategyCommitmentDraftSourceCitationV1;
};

export const STRATEGY_COMMITMENT_DRAFT_PLANNING_ARTIFACT_SCHEMA_VERSION =
  'strategy_commitment_draft_planning_artifact_v1' as const;

export type StrategyCommitmentDraftPlanningArtifactStatusV1 =
  | 'pending'
  | 'approved'
  | 'rejected';

export type StrategyCommitmentDraftPlanningArtifactV1 = {
  schemaVersion: typeof STRATEGY_COMMITMENT_DRAFT_PLANNING_ARTIFACT_SCHEMA_VERSION;
  artifactPosture: typeof STRATEGY_COMMITMENT_DRAFT_ARTIFACT_POSTURE;
  strategyId: string;
  documentId: string;
  sourceName: string;
  draft: StrategyCommitmentDraftV1;
  status: StrategyCommitmentDraftPlanningArtifactStatusV1;
  createdAt: string;
  createdByUserId: string | null;
  updatedAt: string;
  reviewedAt?: string | null;
  reviewedByUserId?: string | null;
  rejectionReason?: string | null;
  approvedCommitmentArtifactId?: string | null;
  revision: number;
};

export type StrategyCommitmentDraftPlanningArtifactListItemV1 =
  StrategyCommitmentDraftPlanningArtifactV1 & {
    id: string;
  };

export type StrategyCommitmentDraftValidationResult =
  | { ok: true; draft: StrategyCommitmentDraftV1 }
  | { ok: false; errors: string[] };

const STRATEGY_COMMITMENT_DRAFT_KEYS = Object.freeze([
  'schemaVersion',
  'artifactPosture',
  'intentText',
  'metricKey',
  'initiativeId',
  'ownerUserId',
  'ownerPosition',
  'baseline',
  'target',
  'targetDate',
  'ownerHint',
  'watchStreams',
  'sourceCitation',
] as const);

const STRATEGY_COMMITMENT_DRAFT_VALUE_KEYS = Object.freeze([
  'value',
  'unit',
  'text',
] as const);

const STRATEGY_COMMITMENT_DRAFT_CITATION_KEYS = Object.freeze([
  'page',
  'section',
] as const);

export function validateStrategyCommitmentDraftV1(
  candidate: unknown,
): StrategyCommitmentDraftValidationResult {
  const errors: string[] = [];

  if (!isRecord(candidate)) {
    return { ok: false, errors: ['draft must be an object'] };
  }

  rejectUnexpectedKeys(candidate, STRATEGY_COMMITMENT_DRAFT_KEYS, 'draft', errors);

  if (candidate.schemaVersion !== STRATEGY_COMMITMENT_DRAFT_SCHEMA_VERSION) {
    errors.push(`schemaVersion must be ${STRATEGY_COMMITMENT_DRAFT_SCHEMA_VERSION}`);
  }

  if (candidate.artifactPosture !== STRATEGY_COMMITMENT_DRAFT_ARTIFACT_POSTURE) {
    errors.push(`artifactPosture must be ${STRATEGY_COMMITMENT_DRAFT_ARTIFACT_POSTURE}`);
  }

  requireNonBlank(candidate.intentText, 'intentText', errors);

  if (!isStrategyCommitmentDraftMetricKey(candidate.metricKey)) {
    errors.push('metricKey must be one of the closed strategy commitment draft metric keys');
  }

  if (candidate.initiativeId !== undefined) {
    requireNonBlank(candidate.initiativeId, 'initiativeId', errors);
  }
  if (candidate.ownerUserId !== undefined) {
    requireNonBlank(candidate.ownerUserId, 'ownerUserId', errors);
  }
  if (candidate.ownerPosition !== undefined && !isCommitmentOwnerPosition(candidate.ownerPosition)) {
    errors.push('ownerPosition requires exact orgStateId and positionId');
  }
  if (candidate.ownerPosition !== undefined && candidate.ownerUserId !== undefined) {
    errors.push('Supply one owner identity: ownerPosition or ownerUserId');
  }

  if (candidate.baseline !== undefined) {
    validateMetricValue(candidate.baseline, 'baseline', errors);
  }

  if (candidate.target === undefined) {
    errors.push('target is required');
  } else {
    validateMetricValue(candidate.target, 'target', errors);
  }

  requireNonBlank(candidate.targetDate, 'targetDate', errors);
  requireNonBlank(candidate.ownerHint, 'ownerHint', errors);
  validateWatchStreams(candidate.watchStreams, errors);
  validateSourceCitation(candidate.sourceCitation, errors);

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  return { ok: true, draft: candidate as StrategyCommitmentDraftV1 };
}

export function isStrategyCommitmentDraftMetricKey(
  value: unknown,
): value is StrategyCommitmentDraftMetricKey {
  return (
    typeof value === 'string' &&
    (STRATEGY_COMMITMENT_DRAFT_METRIC_KEYS as readonly string[]).includes(value)
  );
}

export function isCommitmentOwnerPosition(
  value: unknown,
): value is NonNullable<DecisionLedgerRealizationCommitmentOwnerDto['ownerPosition']> {
  if (!isRecord(value) || Object.keys(value).length !== 2) return false;
  return [value.orgStateId, value.positionId].every(
    (id) => typeof id === 'string' && id.trim().length > 0 && id === id.trim(),
  );
}

function validateMetricValue(
  value: unknown,
  path: 'baseline' | 'target',
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }

  rejectUnexpectedKeys(value, STRATEGY_COMMITMENT_DRAFT_VALUE_KEYS, path, errors);

  if (typeof value.value !== 'number' || !Number.isFinite(value.value)) {
    errors.push(`${path}.value must be a finite number`);
  }

  if (!isStrategyCommitmentDraftMetricUnit(value.unit)) {
    errors.push(`${path}.unit must be one of ${STRATEGY_COMMITMENT_DRAFT_METRIC_UNITS.join(', ')}`);
  }

  requireNonBlank(value.text, `${path}.text`, errors);
}

function validateWatchStreams(value: unknown, errors: string[]): void {
  if (!Array.isArray(value)) {
    errors.push('watchStreams must be an array');
    return;
  }

  for (const stream of value) {
    if (!isStrategyCommitmentDraftWatchStream(stream)) {
      errors.push('watchStreams must contain only closed strategy commitment draft watch streams');
      return;
    }
  }
}

function validateSourceCitation(value: unknown, errors: string[]): void {
  if (!isRecord(value)) {
    errors.push('sourceCitation is required');
    errors.push('sourceCitation.page is required');
    errors.push('sourceCitation.section is required');
    return;
  }

  rejectUnexpectedKeys(value, STRATEGY_COMMITMENT_DRAFT_CITATION_KEYS, 'sourceCitation', errors);

  if (
    typeof value.page !== 'number' ||
    !Number.isInteger(value.page) ||
    value.page < 1
  ) {
    errors.push('sourceCitation.page is required');
  }

  requireNonBlank(value.section, 'sourceCitation.section', errors);
}

function isStrategyCommitmentDraftMetricUnit(
  value: unknown,
): value is StrategyCommitmentDraftMetricUnit {
  return (
    typeof value === 'string' &&
    (STRATEGY_COMMITMENT_DRAFT_METRIC_UNITS as readonly string[]).includes(value)
  );
}

function isStrategyCommitmentDraftWatchStream(
  value: unknown,
): value is StrategyCommitmentDraftWatchStream {
  return (
    typeof value === 'string' &&
    (STRATEGY_COMMITMENT_DRAFT_WATCH_STREAMS as readonly string[]).includes(value)
  );
}

function rejectUnexpectedKeys(
  value: Record<string, unknown>,
  allowedKeys: readonly string[],
  path: string,
  errors: string[],
): void {
  for (const key of Object.keys(value)) {
    if (!allowedKeys.includes(key)) {
      errors.push(`${path}.${key} is not allowed`);
    }
  }
}

function requireNonBlank(value: unknown, path: string, errors: string[]): void {
  if (typeof value !== 'string' || value.trim().length === 0) {
    errors.push(`${path} is required`);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
